import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { Bot, X, Minus, Send, Loader2 } from 'lucide-react';

const Chatbot = ({ currentQuestion, currentOptions, questionNumber, totalQuestions, userCategory = 'Engineering' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Debug: Check URL for API Key restrictions
  useEffect(() => {
    if (isOpen) {
        console.log("Your current URL is:", window.location.origin);
        console.log("Ensure this URL is added to your Google Cloud API Key restrictions!");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = {
        role: 'assistant',
        content: `Hello! I'm your Prepify AI for ${userCategory}. I'm ready to help!`,
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, userCategory]);

  const getQuestionContext = () => {
    if (!currentQuestion) return '';
    
    let context = `Current Question ${questionNumber} of ${totalQuestions}:\n\n`;
    context += `Question: ${currentQuestion}\n\n`;
    context += `Options:\n`;
    if (currentOptions && Array.isArray(currentOptions)) {
        currentOptions.forEach((option, index) => {
        context += `${String.fromCharCode(65 + index)}. ${option}\n`;
        });
    }
    return context;
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    
    if (!apiKey) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'API key not configured. Please check your .env file.',
      }]);
      return;
    }

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const genAI = new GoogleGenerativeAI(apiKey);
      
      // SWITCHING TO 'gemini-pro' (Most stable/standard model)
      // If this works, we can try flash later. 
      const model = genAI.getGenerativeModel({ model: 'gemini-pro' });

      const questionContext = getQuestionContext();
      
      const prompt = `
      You are "Prepify AI", an expert NUST Entry Test tutor.
      Subject: ${userCategory}

      CONTEXT:
      ${questionContext}

      STUDENT QUESTION: 
      ${userMessage}

      INSTRUCTIONS:
      1. Help the student understand the concept.
      2. Be concise and encouraging.
      3. Do not give the answer immediately, guide them.
      `;

      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();

      setMessages(prev => [...prev, { role: 'assistant', content: text }]);

    } catch (error) {
      console.error('Gemini API Error:', error);
      
      // Show the ACTUAL error message from Google to help debugging
      let errorMsg = `System Error: ${error.message}`;
      
      if (error.message.includes('404')) {
        errorMsg = "Error (404): The model is not reachable. This usually means your API Key has 'Website Restrictions' that don't match your current URL.";
      }
      
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  if (!apiKey) return null;

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 left-6 z-50 bg-gradient-to-r from-blue-600 to-purple-700 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110 active:scale-95 flex items-center justify-center group"
        >
          <Bot className="text-2xl group-hover:animate-pulse" />
          <span className="absolute -top-2 -right-2 bg-green-500 w-3 h-3 rounded-full animate-pulse"></span>
        </button>
      )}

      {isOpen && (
        <div className={`fixed bottom-6 left-6 z-50 bg-white rounded-2xl shadow-2xl transition-all duration-300 ${isMinimized ? 'w-80 h-16' : 'w-96 h-[600px] max-h-[80vh]'} flex flex-col border-2 border-blue-200`}>
          <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bot className="text-xl" />
              <div>
                <h3 className="font-semibold text-sm sm:text-base">Prepify AI Tutor</h3>
                {!isMinimized && <p className="text-xs text-blue-100">{userCategory} Mode • Q{questionNumber}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setIsMinimized(!isMinimized)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <Minus className="text-sm" />
              </button>
              <button onClick={() => { setIsOpen(false); setIsMinimized(false); }} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <X className="text-sm" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((message, index) => (
                  <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-2xl p-3 ${message.role === 'user' ? 'bg-gradient-to-r from-blue-600 to-purple-700 text-white' : 'bg-white text-gray-800 border-2 border-blue-200'}`}>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white border-2 border-blue-200 rounded-2xl p-3">
                      <Loader2 className="text-blue-600 animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-4 bg-white border-t border-gray-200 rounded-b-2xl">
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about this question..."
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-500 text-sm"
                    disabled={isLoading}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    className="bg-gradient-to-r from-blue-600 to-purple-700 text-white p-2 rounded-xl hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 active:scale-95"
                  >
                    {isLoading ? <Loader2 className="animate-spin" /> : <Send />}
                  </button>
                </div>
                <button onClick={clearChat} className="mt-2 text-xs text-gray-500 hover:text-gray-700 transition-colors">
                  Clear chat
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

Chatbot.propTypes = {
  currentQuestion: PropTypes.string,
  currentOptions: PropTypes.arrayOf(PropTypes.string),
  questionNumber: PropTypes.number,
  totalQuestions: PropTypes.number,
  userCategory: PropTypes.string,
};

export default Chatbot;